import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";

import "components/pages/transmissions/modal/next_send/NextSendModal.css";

import LoadingSpinner from "components/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import {
  changeNextSendDate,
  changePatient,
  setCacheRefresh,
  toggleModal,
  toggleProcessing,
  toggleFetchTableData,
  changeNextSendInformation,
} from "app/store/transmission/slice/transmissionSlice";
import { useUpdateNextSendMutation } from "app/store/transmission/api/transmissionTableApi";
import { removeCache } from "app/utils/cache";
import { Alert, FormControlLabel, MenuItem, TextField } from "@mui/material";
import {
  getDay31BillingCodes,
  getCustomBillingCodes,
  DAY91_BILLING_CODE_OPTIONS,
  TOAST_SEVERITY,
} from "static/constants";
import SubmitButton from "components/Button/SubmitButton";
import { showErrorToast, showSuccessToast } from "utils/toast";
import moment from "moment";
import { cloneDeep, isEqual } from "lodash";
import { patientByPatientId } from "app/services/transmission/patient";
import { getFormattedDate, isDateInPast, isValidDateFormat } from "utils/date";
import FormFieldError from "components/common/form_error/form_field_error";
import { getNumberInputValue } from "utils/number";
import dayjs from "dayjs";
import {
  checkIsIDTFBilling,
  getMappedBillingCode,
} from "utils/billingCode.util";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { DATE_FORMAT } from "static/date";

const EditNextSendModal = () => {
  const { isNextSendModal, isProcessing, patientId, nextSendInformation } =
    useSelector((state) => state.transmission);

  const { accountsMapper } = useSelector((state) => state.account);
  const isIDTFBilling = checkIsIDTFBilling({
    account: accountsMapper[nextSendInformation?.account],
    patient: nextSendInformation,
  });

  const initialValues = {
    nextSendDate: null,
    nextSendDays: 0,
    nextBillingDate: null,
    nextBillingDateChanged: false,
    day31: false,
    day31BillingCode: "",
    day91: false,
    day91BillingCode: "",
    customDays: false,
    customBillingDays: "",
    customDaysBillingCode: "",
  };

  const dispatch = useDispatch();
  const [updateNextSend] = useUpdateNextSendMutation();

  const [dateErrorExist, setDateErrorExist] = useState(false);
  const [stateQueue, setStateQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [nextSendAndBillingFormValues, setNextSendAndBillingFormValues] =
    useState(initialValues);
  const [isNextBillingUpdated, setIsNextBillingUpdated] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState([]);
  const [selectedCustomBillingCode, setSelectedCustomBillingCode] = useState();
  const [isCustomBillingEnabled, setIsCustomBillingEnabled] = useState(false);

  useEffect(() => {
    const fetchPatientNextSend = async () => {
      try {
        setIsFetching(true);
        const { data: nextSendInformation } =
          await patientByPatientId(patientId);
        const { implanted_billing_schedule } = nextSendInformation;

        setIsCustomBillingEnabled(
          implanted_billing_schedule?.day_custom?.active
        );
        setSelectedCustomBillingCode(
          implanted_billing_schedule?.day_custom?.billing_code
        );
        dispatch(changeNextSendInformation(nextSendInformation));
      } catch (error) {
        showErrorToast(error?.message);
      } finally {
        setIsFetching(false);
      }
    };

    if (isNextSendModal) {
      fetchPatientNextSend();
    }
  }, [dispatch, isNextSendModal, patientId]);

  useEffect(() => {
    const processStateQueue = () => {
      for (const update of stateQueue) {
        setNextSendAndBillingFormValues((prevValues) => ({
          ...prevValues,
          ...update,
        }));
      }
      setStateQueue([]);
    };

    if (stateQueue.length > 0) {
      processStateQueue();
    }
  }, [stateQueue]);

  useEffect(() => {
    if (nextSendInformation) {
      setNextSendAndBillingFormValues({
        ...nextSendAndBillingFormValues,
        ...nextSendInformation,
      });
      updateSelectedBillingCycle(nextSendInformation);
    }
  }, [nextSendInformation]);

  const updateSelectedBillingCycle = (nextSendInformation) => {
    const { day31, day91, customDays, customBillingDays } = nextSendInformation;
    const updatedBillingCycle = [
      { cycle: "day31", status: day31, priority: 1 },
      { cycle: "day91", status: day91, priority: 2 },
      {
        cycle: "customDays",
        status: customDays,
        days: customBillingDays,
        priority: 3,
      },
    ];
    setSelectedBillingCycle(updatedBillingCycle);
  };

  const onConfirm = async () => {
    try {
      setIsLoading(true);
      const {
        nextSendDate,
        nextSendDays,
        nextBillingDate,
        nextBillingDateChanged,
      } = nextSendAndBillingFormValues;

      const billingSchedule = getBillingSchedule();
      const payload = {
        patientId,
        billingSchedule,
        nextBillingDateChanged,
        nextSendDays: Number(nextSendDays),
        nextSendDate: nextSendDate
          ? moment(nextSendDate).format("YYYY-MM-DD")
          : null,
        nextBillingDate: nextBillingDate
          ? moment(nextBillingDate).format("YYYY-MM-DD")
          : null,
      };

      const result = await updateNextSend(payload).unwrap();
      if (result) {
        const { error, message } = result;
        error
          ? showErrorToast(message, { type: TOAST_SEVERITY.WARNING })
          : showSuccessToast(message);
        await removeCache();
        await dispatch(toggleFetchTableData(true));
        dispatch(setCacheRefresh(true));
      }
      onClose();
      dispatch(
        changePatient({
          patientId: "",
          serialNumber: "",
          account: "",
        })
      );
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setIsLoading(false);
      dispatch(toggleFetchTableData(false));
      dispatch(setCacheRefresh(false));
    }
  };

  const handleFormUpdate = (name, value) => {
    setStateQueue((prevQueue) => [...prevQueue, { [name]: value }]);
  };

  /**
   * This function verifies if the chosen custom billing code is already selected within the 31-day or 91-day billing code.
   * If the code exists in the 31-day or 91-day billing list, the respective 31-day or 91-day billing field is deactivated.
   * @param {*} customBillingCode
   */
  const handleCustomBillingCodeSelect = (
    customBillingCode,
    nextBillingScheduleValues
  ) => {
    const copy = cloneDeep(nextBillingScheduleValues);

    setIsCustomBillingEnabled(copy.customDays);
    setSelectedCustomBillingCode(customBillingCode);

    if (copy.customDays && customBillingCode) {
      if (copy.day31BillingCode === customBillingCode) {
        copy.day31 = false;
      }

      if (copy.day91BillingCode === customBillingCode) {
        copy.day91 = false;
      }
    }

    copy.customDaysBillingCode = customBillingCode;

    setStateQueue((prevQueue) => [...prevQueue, { ...copy }]);
  };

  /**
   * This function verifies whether the chosen 31-day or 91-day billing code has been previously selected within the custom billing codes.
   * If the code exists in the custom billing list, it resets the respective 31-day or 91-day billing field.
   * @param {*} is31Day
   */
  const handleBillingCycleCheckbox = (is31Day = false) => {
    const copy = cloneDeep(nextSendAndBillingFormValues);

    if (is31Day) {
      copy.day31 = !nextSendAndBillingFormValues.day31;
      const isCustomAnd31DaySame =
        isCustomBillingEnabled &&
        copy.day31BillingCode === selectedCustomBillingCode;
      copy.day31BillingCode = isCustomAnd31DaySame ? "" : copy.day31BillingCode;
    } else {
      copy.day91 = !nextSendAndBillingFormValues.day91;
      const isCustomAnd91DaySame =
        isCustomBillingEnabled &&
        copy.day91BillingCode === selectedCustomBillingCode;
      copy.day91BillingCode = isCustomAnd91DaySame ? "" : copy.day91BillingCode;
    }

    setStateQueue((prevQueue) => [...prevQueue, { ...copy }]);
  };

  const getBillingSchedule = () => {
    const billingSchedule = [];

    if (nextSendAndBillingFormValues.day31BillingCode) {
      billingSchedule.push({
        days: 31,
        active: nextSendAndBillingFormValues.day31,
        billingCode: nextSendAndBillingFormValues.day31BillingCode,
        isCustom: false,
      });
    }

    if (nextSendAndBillingFormValues.day91BillingCode) {
      billingSchedule.push({
        days: 91,
        active: nextSendAndBillingFormValues.day91,
        billingCode: nextSendAndBillingFormValues.day91BillingCode,
        isCustom: false,
      });
    }

    if (
      nextSendAndBillingFormValues.customBillingDays &&
      nextSendAndBillingFormValues.customDaysBillingCode
    ) {
      billingSchedule.push({
        days: Number(nextSendAndBillingFormValues.customBillingDays),
        active: nextSendAndBillingFormValues.customDays,
        billingCode: nextSendAndBillingFormValues.customDaysBillingCode,
        isCustom: true,
      });
    }

    return billingSchedule;
  };

  const handleNextBillingDateUpdate = (date) => {
    handleFormUpdate("nextBillingDate", date);
    handleFormUpdate("nextBillingDateChanged", true);
    setIsNextBillingUpdated(true);
    handleFormUpdate("nextSendDate", date);
  };

  const handleBillingCycleUpdate = (name, value) => {
    handleFormUpdate(name, value);

    if (isNextBillingUpdated) {
      const indexToUpdate = selectedBillingCycle.findIndex(
        (billingCycle) => billingCycle.cycle === name
      );
      let updatedBillingCycle;
      if (indexToUpdate !== -1) {
        updatedBillingCycle = selectedBillingCycle.map((billingCycle, index) =>
          index === indexToUpdate
            ? { ...billingCycle, status: value }
            : billingCycle
        );
      }
      if (name === "customBillingDays") {
        const indexToUpdate = selectedBillingCycle.findIndex(
          (billingCycle) => billingCycle.cycle === "customDays"
        );
        updatedBillingCycle = selectedBillingCycle.map((billingCycle, index) =>
          index === indexToUpdate
            ? { ...billingCycle, days: Number(value) }
            : billingCycle
        );
      }
      setSelectedBillingCycle(updatedBillingCycle);
      setBillingCycleWithHighestPriority(updatedBillingCycle);
    }

    if (name === "customDays") {
      setIsCustomBillingEnabled(value);
    }
  };

  const setBillingCycleWithHighestPriority = (billingCycle) => {
    const highestPriorityBillingCycle = billingCycle
      .filter((item) => item.status === true)
      .sort((a, b) => a.priority - b.priority)
      .find(() => true);

    let cycleDays = 31;
    if (highestPriorityBillingCycle) {
      if (highestPriorityBillingCycle.cycle === "day31") {
        cycleDays = 31;
      } else if (highestPriorityBillingCycle.cycle === "day91") {
        cycleDays = 91;
      } else if (
        highestPriorityBillingCycle.cycle === "customDays" &&
        highestPriorityBillingCycle.days
      ) {
        cycleDays = highestPriorityBillingCycle.days;
      }
    }
    handleFormUpdate("nextSendDays", cycleDays);
  };

  const onClose = () => {
    setNextSendAndBillingFormValues(initialValues);
    setIsNextBillingUpdated(false);
    dispatch(toggleProcessing(false));
    dispatch(changeNextSendDate(""));
    dispatch(toggleModal({ type: "nextSend", value: false }));
  };

  const checkSubmitAllowed = () => {
    const {
      day31,
      day91,
      customDays,
      nextSendDays,
      nextSendDate,
      nextBillingDate,
      day31BillingCode,
      day91BillingCode,
      customBillingDays,
      customDaysBillingCode,
    } = nextSendAndBillingFormValues;

    // check if form has changes
    const hasFormChanged = !isEqual(
      nextSendInformation,
      nextSendAndBillingFormValues
    );

    // check if nextSendDate is of valid format
    let isNextSendDateValid =
      nextSendDate && isValidDateFormat(nextSendDate, "MM/DD/YYYY");

    // if nextSendDate is updated, check if it is older than today's date
    if (
      nextSendInformation &&
      nextSendDate !== nextSendInformation.nextSendDate
    ) {
      isNextSendDateValid = !isDateInPast(nextSendDate);
    }

    // check if nextSendDays exists and is greater than 0 and less than 366
    const isNextSendDaysValid =
      nextSendDays && nextSendDays > 0 && nextSendDays < 366;

    // check if nextBillingDate is of valid format only if it exists
    let isNextBillingDateValid = true;
    if (nextBillingDate) {
      isNextBillingDateValid =
        isValidDateFormat(nextBillingDate, "MM/DD/YYYY") &&
        !isDateInPast(nextBillingDate);
    }

    // if nextBillingDate is updated, check if it is older than today's date
    if (
      nextSendInformation &&
      nextBillingDate !== nextSendInformation.nextBillingDate
    ) {
      isNextBillingDateValid = !isDateInPast(nextBillingDate);
    }

    /**
     * check billingSchedule validity
     * 1. if day31 is active, day31BillingCode must have a value
     * 2. if day91 is active, day91BillingCode must have a value
     * 3. if customDays is active, customDaysBillingCode must have a value and customBillingDays must have a value
     */
    let isBillingScheduleValid = true;
    if (day31) {
      isBillingScheduleValid = day31BillingCode?.length > 0;
    } else if (day91) {
      isBillingScheduleValid = day91BillingCode?.length > 0;
    } else if (customDays) {
      isBillingScheduleValid =
        customDaysBillingCode?.length > 0 && customBillingDays > 0;
    }

    // if any of the above conditions are false, disallow submit
    const isInvalid =
      dateErrorExist ||
      !hasFormChanged ||
      !isNextSendDateValid ||
      !isNextSendDaysValid ||
      !isBillingScheduleValid ||
      !isNextBillingDateValid;

    return isInvalid;
  };

  const handleResetBilling = () => {
    const {
      nextSendDate: currentNextSendDate,
      nextSendDays: currentNextSendDays,
    } = nextSendAndBillingFormValues;

    setNextSendAndBillingFormValues({
      ...nextSendInformation,
      nextSendDate: currentNextSendDate,
      nextSendDays: currentNextSendDays,
    });
  };

  return (
    <ModalWrapper
      show={isNextSendModal}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title="Edit Next Send Date or Billing Date"
    >
      {isFetching ? (
        <Grid
          container
          item
          justifyContent="center"
          alignItems="center"
          style={{ height: "500px" }}
        >
          <LoadingSpinner />
        </Grid>
      ) : (
        <>
          <div className="mt-3 mb-3">
            <Grid container className="edit-next-send-section" spacing={1}>
              <Grid item xs={12}>
                <span className="edit-next-send-title">Next Send Date</span>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <DatePicker
                    autoOk
                    required
                    margin="normal"
                    variant="inline"
                    format={DATE_FORMAT.DEFAULT}
                    label="Next Send Date"
                    id="last_billing_date"
                    inputVariant="outlined"
                    className="trans-table__filterDatePicker"
                    value={
                      nextSendAndBillingFormValues.nextSendDate
                        ? dayjs(nextSendAndBillingFormValues.nextSendDate)
                        : null
                    }
                    KeyboardButtonProps={{
                      "aria-label": "pick date",
                    }}
                    onChange={(date) => {
                      handleFormUpdate("nextSendDate", getFormattedDate(date));
                      setIsNextBillingUpdated(false);
                      handleResetBilling();
                    }}
                    onError={(error) => setDateErrorExist(Boolean(error))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      ".MuiInputBase-input": {
                        height: "auto !important",
                      },
                    }}
                  />
                  {nextSendInformation &&
                    nextSendAndBillingFormValues.nextSendDate !==
                      nextSendInformation.nextSendDate &&
                    isDateInPast(nextSendAndBillingFormValues.nextSendDate) && (
                      <FormFieldError errorMessage="Next Send Date cannot be older than today's date" />
                    )}
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  type="number"
                  required
                  value={nextSendAndBillingFormValues.nextSendDays}
                  label="Next Send Cycle (Days)"
                  name="nextSendDays"
                  onChange={(e) => {
                    const { name, value } = e.target;
                    const nextSendDaysValue = getNumberInputValue(value);
                    handleFormUpdate(name, nextSendDaysValue);
                    handleResetBilling();
                  }}
                />
              </Grid>

              {isNextBillingUpdated && (
                <Grid item xs={12}>
                  <Alert severity="warning" className="mt-3 mb-3">
                    Next send date auto-adjusted to
                    <strong>
                      {" "}
                      {moment(
                        nextSendAndBillingFormValues?.nextSendDate
                      ).format("MM/DD/YYYY")}{" "}
                    </strong>
                  </Alert>
                </Grid>
              )}
            </Grid>

            <Grid
              container
              className="edit-next-billing-section mt-2"
              spacing={1}
            >
              <Grid item xs={12}>
                <span className="edit-next-send-title">Next Billing Date</span>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  autoOk
                  required
                  margin="normal"
                  variant="inline"
                  format={DATE_FORMAT.DEFAULT}
                  label="Next Bill Date"
                  id="last_billing_date"
                  inputVariant="outlined"
                  className="trans-table__filterDatePicker"
                  value={
                    nextSendAndBillingFormValues.nextBillingDate
                      ? dayjs(nextSendAndBillingFormValues.nextBillingDate)
                      : null
                  }
                  KeyboardButtonProps={{
                    "aria-label": "pick date",
                  }}
                  onChange={(date) => {
                    handleNextBillingDateUpdate(getFormattedDate(date));
                  }}
                  onError={(error) => setDateErrorExist(Boolean(error))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
                {nextSendInformation &&
                  nextSendAndBillingFormValues.nextBillingDate !==
                    nextSendInformation.nextBillingDate &&
                  isDateInPast(
                    nextSendAndBillingFormValues.nextBillingDate
                  ) && (
                    <FormFieldError errorMessage="Next Billing Date cannot be older than today's date" />
                  )}
              </Grid>
              {
                <>
                  <Grid item xs={12}>
                    <span className="edit-next-send-title">
                      Schedule - Billing Cycle
                    </span>
                  </Grid>

                  <Grid
                    container
                    item
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Grid item xs={6} style={{ textAlign: "left" }}>
                      <FormControlLabel
                        label="31 day"
                        control={
                          <Checkbox
                            name="day31"
                            checked={nextSendAndBillingFormValues.day31}
                            onChange={(e) => {
                              handleBillingCycleCheckbox(true);
                              handleBillingCycleUpdate(
                                e.target.name,
                                !nextSendAndBillingFormValues.day31
                              );
                            }}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={6} style={{ textAlign: "left" }}>
                      <FormControlLabel
                        label="91 day"
                        control={
                          <Checkbox
                            name="day91"
                            checked={nextSendAndBillingFormValues.day91}
                            onChange={(e) => {
                              handleBillingCycleCheckbox(false);
                              handleBillingCycleUpdate(
                                e.target.name,
                                !nextSendAndBillingFormValues.day91
                              );
                            }}
                          />
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    item
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Grid item xs={6}>
                      <FormControl
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                      >
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ background: "white" }}
                        >
                          31 day Billing Code
                        </InputLabel>
                        <Select
                          style={{ textAlign: "left" }}
                          id="active"
                          value={getMappedBillingCode({
                            billingCode:
                              nextSendAndBillingFormValues.day31BillingCode,
                            isIDTFBilling,
                          })}
                          name="day31BillingCode"
                          variant="outlined"
                          onChange={(event) => {
                            const { name, value } = event.target;
                            handleFormUpdate(name, value);
                          }}
                          disabled={!nextSendAndBillingFormValues.day31}
                        >
                          {getDay31BillingCodes(isIDTFBilling).map((code) => (
                            <MenuItem
                              key={code}
                              value={code}
                              disabled={
                                isCustomBillingEnabled &&
                                code === selectedCustomBillingCode
                              }
                            >
                              {code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                      >
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ background: "white" }}
                        >
                          91 day Billing Code
                        </InputLabel>
                        <Select
                          style={{ textAlign: "left" }}
                          id="active"
                          value={getMappedBillingCode({
                            billingCode:
                              nextSendAndBillingFormValues.day91BillingCode,
                            isIDTFBilling,
                          })}
                          name="day91BillingCode"
                          variant="outlined"
                          disabled={!nextSendAndBillingFormValues.day91}
                          onChange={(event) => {
                            const { name, value } = event.target;
                            handleFormUpdate(name, value);
                          }}
                        >
                          {DAY91_BILLING_CODE_OPTIONS.map((code) => (
                            <MenuItem
                              value={code}
                              disabled={
                                isCustomBillingEnabled &&
                                code === selectedCustomBillingCode
                              }
                            >
                              {code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Grid container item justifyContent="space-between">
                    <Grid item xs={12} style={{ textAlign: "left" }}>
                      <FormControlLabel
                        label="Custom"
                        name="customDays"
                        margin
                        control={
                          <Checkbox
                            checked={nextSendAndBillingFormValues.customDays}
                            onChange={(e) => {
                              const isCustomDaysEnabled =
                                !nextSendAndBillingFormValues.customDays;
                              const updatedNextSendAndBillingFormValues = {
                                ...nextSendAndBillingFormValues,
                                customDays: isCustomDaysEnabled,
                              };

                              handleBillingCycleUpdate(
                                e.target.name,
                                isCustomDaysEnabled
                              );
                              handleCustomBillingCodeSelect(
                                nextSendAndBillingFormValues.customDaysBillingCode,
                                updatedNextSendAndBillingFormValues
                              );
                            }}
                          />
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    item
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        variant="outlined"
                        label="Custom Days"
                        name="customBillingDays"
                        value={nextSendAndBillingFormValues.customBillingDays}
                        disabled={!nextSendAndBillingFormValues.customDays}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          const customDaysValue = getNumberInputValue(value);
                          handleBillingCycleUpdate(name, customDaysValue);
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl required fullWidth variant="outlined">
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ background: "white" }}
                        >
                          Custom Billing Code
                        </InputLabel>
                        <Select
                          id="active"
                          name="customDaysBillingCode"
                          variant="outlined"
                          style={{ textAlign: "left" }}
                          disabled={!nextSendAndBillingFormValues.customDays}
                          value={getMappedBillingCode({
                            billingCode:
                              nextSendAndBillingFormValues.customDaysBillingCode,
                            isIDTFBilling,
                          })}
                          onChange={(e) => {
                            const { value } = e.target;
                            handleCustomBillingCodeSelect(
                              value,
                              nextSendAndBillingFormValues
                            );
                          }}
                        >
                          {getCustomBillingCodes(isIDTFBilling).map((code) => (
                            <MenuItem key={code} value={code}>
                              {code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              }
            </Grid>
          </div>
          <Alert severity="warning" className="mt-3 mb-3">
            Updating the Next Bill Date will auto adjust the Next Send Date.
          </Alert>

          {isProcessing && (
            <div>
              <div className="edit-next-send-modal__processing">
                Saving Next Send Date.
              </div>
              <div className="edit-next-send-modal__spinner">
                <LoadingSpinner color="secondary" />
              </div>
            </div>
          )}
          <div className="d-flex flex-end mt-3" style={{ gap: "10px" }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>

            <SubmitButton
              variant="contained"
              color="secondary"
              handleClick={onConfirm}
              isLoading={isLoading}
              disabled={checkSubmitAllowed()}
            />
          </div>
        </>
      )}
    </ModalWrapper>
  );
};

export default EditNextSendModal;
